import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { AccountForm } from "@/components/AccountForm";
import { LogoutButton } from "@/components/AuthGate";

const PW_HINT = "12–72 characters. Sent over HTTPS and hashed on the server.";
const backToLogin = () => window.location.assign("/");

const BootstrapPanel = ({ me, reload }) => {
  const [created, setCreated] = useState(false);
  if (me.permanent_exists || created) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-emerald-900" data-testid="permanent-created-notice">
        <div className="font-medium">Your permanent account exists.</div>
        <p className="text-sm mt-1">Log out, sign in with the permanent account, then come back here to remove this bootstrap account.</p>
        <LogoutButton className="mt-4 bg-emerald-800 text-white px-4 py-2 rounded-lg hover:bg-emerald-700" />
      </div>
    );
  }
  return (
    <AccountForm testid="create-permanent" title="Create permanent account" hint={PW_HINT} url="/account/permanent"
      submitLabel="Create permanent account" onDone={() => { setCreated(true); reload(); }}
      fields={[{ key: "username", label: "Username", type: "text", auto: "off" },
               { key: "password", label: "Password", auto: "new-password" },
               { key: "confirm_password", label: "Confirm password", auto: "new-password" }]} />
  );
};

const PermanentPanel = ({ me, reload }) => (
  <div className="space-y-6">
    {me.bootstrap_active && (
      <AccountForm testid="remove-bootstrap" danger title="Remove bootstrap account"
        hint="Permanently removes the initial bootstrap login and signs out its sessions. It will not come back after restarts or deployments. Type REMOVE BOOTSTRAP to confirm."
        url="/account/bootstrap/remove" submitLabel="Remove bootstrap account" onDone={reload}
        fields={[{ key: "confirm", label: "Confirmation", type: "text", auto: "off" }]} />
    )}
    <AccountForm testid="change-username" title="Change username" hint="You'll be signed out and must log in again."
      url="/account/username" submitLabel="Change username" onDone={backToLogin}
      fields={[{ key: "new_username", label: "New username", type: "text", auto: "off" },
               { key: "current_password", label: "Current password", auto: "current-password" }]} />
    <AccountForm testid="change-password" title="Change password" hint={`${PW_HINT} All sessions are signed out.`}
      url="/account/password" submitLabel="Change password" onDone={backToLogin}
      fields={[{ key: "current_password", label: "Current password", auto: "current-password" },
               { key: "new_password", label: "New password", auto: "new-password" },
               { key: "confirm_password", label: "Confirm new password", auto: "new-password" }]} />
  </div>
);

export default function AccountPage() {
  const [me, setMe] = useState(null);
  const load = useCallback(() => api.get("/account").then(({ data }) => setMe(data)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="min-h-screen bg-stone-100 px-10 py-12" data-testid="account-page">
      <div className="max-w-xl space-y-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900" data-testid="account-back">
          <ArrowLeft className="w-4 h-4" /> Back to snapshots
        </Link>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Account</div>
          {me && (
            <div className="font-display text-3xl text-stone-900 mt-2">
              <span data-testid="account-username">{me.username}</span>
              <span className="ml-3 align-middle text-xs uppercase tracking-wider px-2 py-1 rounded-full bg-stone-200 text-stone-600" data-testid="account-kind">
                {me.kind === "bootstrap" ? "Bootstrap" : "Permanent"}
              </span>
            </div>
          )}
        </div>
        {me && (me.kind === "bootstrap" ? <BootstrapPanel me={me} reload={load} /> : <PermanentPanel me={me} reload={load} />)}
      </div>
    </div>
  );
}
